import React, { memo } from 'react';
import PropTypes from 'prop-types';
import CustomTextInput from 'components/partials/custom-text-input/custom-text-input';
import { STYLES } from '../constants/styles.constants';
import { ADMIT_TABLE_HEADERS, YIELD_STYLES } from '../constants/admit-table.constants';

const AdmitTableViewer = memo(({ needRank, academicData, summaryValues, onPercentageChange, onPercentageBlur }) => {
  
  // Handle focus to select all text if value is "0" or "0.0"
  const handleFocus = (event) => {
    if (event.target.value === '0' || event.target.value === '0.0') {
      event.target.select();
    }
  };

  // Render conditional styling for projected yield comparison
  const renderProjectedYieldStyling = () => {
    if (!summaryValues?.pyYield || !summaryValues?.projectedYield) return YIELD_STYLES.default;
    const pyYield = parseFloat(summaryValues.pyYield);
    const projYield = parseFloat(summaryValues.projectedYield);
    if (projYield > pyYield) return YIELD_STYLES.positive;
    if (projYield < pyYield) return YIELD_STYLES.negative;
    return YIELD_STYLES.default;
  };

  // Render table header
  const renderTableHeader = (headers) => (
    <thead>
      <tr className={STYLES.table.headerRow}>
        {headers.map(({ key, label }) => (
          <th key={key} className={STYLES.table.headerCell}>
            {label}
          </th>
        ))}
      </tr>
    </thead>
  );

  return (
    <div className="mt-6 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-blue-03 mb-4">{needRank.label}</h2>
        <div className="flex gap-4 w-full">
          {/* Main Table */}
          <div className={`flex-grow w-full ${STYLES.table.container}`}>
            <table className="w-full table-fixed">
              {renderTableHeader(ADMIT_TABLE_HEADERS.main)}
              <tbody>
                {academicData.map((row, index) => (
                  <tr key={index} className={STYLES.table.bodyRow}>
                    <td className={STYLES.table.bodyCell}>{row.rank}</td>
                    <td className={STYLES.table.bodyCell}>{row.admits}</td>
                    <td className={STYLES.table.bodyCell}>
                      <div className={STYLES.table.inputWrapper}>
                        <CustomTextInput
                          type="text"
                          value={row.increase}
                          onChange={(e) => onPercentageChange(needRank.min, index, 'increase', e.target.value)}
                          onBlur={() => onPercentageBlur(needRank.min, index, 'increase')}
                          onFocus={handleFocus}
                          inputCustomClass={STYLES.table.input}
                          inputWrapperCustomClass="w-full"
                        />
                        <span className={STYLES.table.percentageSymbol}>%</span>
                      </div>
                    </td>
                    <td className={STYLES.table.bodyCell}>{row.target}</td>
                    <td className={STYLES.table.bodyCell}>
                      <div className={STYLES.table.inputWrapper}>
                        <CustomTextInput
                          type="text"
                          value={row.yield}
                          onChange={(e) => onPercentageChange(needRank.min, index, 'yield', e.target.value)}
                          onBlur={() => onPercentageBlur(needRank.min, index, 'yield')}
                          onFocus={handleFocus}
                          inputCustomClass={STYLES.table.input}
                          inputWrapperCustomClass="w-full"
                        />
                        <span className={STYLES.table.percentageSymbol}>%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Yield Table */}
          <div className={`w-56 flex-shrink-0 ${STYLES.table.container}`}>
            <table className="w-full table-fixed">
              {renderTableHeader(ADMIT_TABLE_HEADERS.yield)}
              <tbody>
                {academicData.map((row, index) => (
                  <tr key={index} className={STYLES.table.bodyRow}>
                    <td className="px-3 py-2 h-10 text-blue-03 text-sm">{row.yieldPY}</td>
                    <td className="px-3 py-2 h-10 text-blue-03 text-sm">{row.projectedYield}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Summary Yield */}
          <div className={`w-48 flex-shrink-0 ${STYLES.table.container}`}>
            <table className="w-full table-fixed">
              {renderTableHeader(ADMIT_TABLE_HEADERS.summary)}
              <tbody>
                <tr className={STYLES.table.bodyRow}>
                  <td className="px-3 py-2 text-blue-03 text-sm">{summaryValues?.pyYield || '0.00%'}</td>
                  <td className={`px-3 py-2 text-sm font-medium ${renderProjectedYieldStyling()}`}>
                    {summaryValues?.projectedYield || '0.00%'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
});

AdmitTableViewer.propTypes = {
  needRank: PropTypes.shape({
    label: PropTypes.string.isRequired,
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
  }).isRequired,
  academicData: PropTypes.arrayOf(
    PropTypes.shape({
      rank: PropTypes.number.isRequired,
      admits: PropTypes.number.isRequired,
      target: PropTypes.number.isRequired,
      yieldPY: PropTypes.string.isRequired,
      projectedYield: PropTypes.string.isRequired,
      increase: PropTypes.string,
      yield: PropTypes.string,
    })
  ).isRequired,
  summaryValues: PropTypes.shape({
    pyYield: PropTypes.string.isRequired,
    projectedYield: PropTypes.string.isRequired,
  }),
  onPercentageChange: PropTypes.func.isRequired,
  onPercentageBlur: PropTypes.func.isRequired,
};

export default AdmitTableViewer; 